/**
 * Canonical mealtime metadata for the whole Ordo client — the single source of
 * truth for the set of mealtimes, their display labels, and their chronological
 * (time-of-day) ordering. Mirrors the Prisma `Mealtime` enum
 * (`packages/schema/schema/catalog.prisma`); keep this in sync if the enum
 * changes. Pure (no imports) so every layer — api wire types, lib helpers,
 * components — can depend on it without cycles.
 *
 * Ordering is by time of day, NOT enum declaration order:
 *   Breakfast → AM Snack → Lunch → Snack → PM Snack → Supper
 */

export type Mealtime = 'BREAKFAST' | 'AM_SNACK' | 'LUNCH' | 'SNACK' | 'PM_SNACK' | 'SUPPER'

/** All mealtimes in time-of-day order. Iterate / render in this order. */
export const MEALTIMES: readonly Mealtime[] = ['BREAKFAST', 'AM_SNACK', 'LUNCH', 'SNACK', 'PM_SNACK', 'SUPPER']

/**
 * Parse an untrusted mealtime string (URL param, storage) against the FULL
 * six-value set. Case-insensitive; null for anything else. Exists because a
 * hand-rolled four-value allowlist in the serve print silently rewrote
 * AM_SNACK / PM_SNACK links to LUNCH — validate against MEALTIMES, never a
 * local subset.
 */
export function parseMealtimeParam(raw: string | null | undefined): Mealtime | null {
  if (!raw) {
    return null
  }
  const upper = raw.toUpperCase()
  return (MEALTIMES as readonly string[]).includes(upper) ? (upper as Mealtime) : null
}

export const MEALTIME_LABELS: Record<Mealtime, string> = {
  BREAKFAST: 'Breakfast',
  AM_SNACK: 'AM Snack',
  LUNCH: 'Lunch',
  SNACK: 'Snack',
  PM_SNACK: 'PM Snack',
  SUPPER: 'Supper',
}

/** Short forms for dense chips (e.g. collapsed day-card summaries). */
export const MEALTIME_ABBR: Record<Mealtime, string> = {
  BREAKFAST: 'B',
  AM_SNACK: 'AS',
  LUNCH: 'L',
  SNACK: 'SN',
  PM_SNACK: 'PS',
  SUPPER: 'SP',
}

const MEALTIME_INDEX: Record<Mealtime, number> = MEALTIMES.reduce<Record<string, number>>((acc, mealtime, index) => {
  acc[mealtime] = index
  return acc
}, {}) as Record<Mealtime, number>

/** Chronological sort index for a mealtime (unknown values sort last). */
export function mealtimeOrder(mealtime: Mealtime): number {
  return MEALTIME_INDEX[mealtime] ?? Number.MAX_SAFE_INTEGER
}

/** Comparator for `Array.prototype.sort` — chronological time-of-day order. */
export function compareMealtimes(a: Mealtime, b: Mealtime): number {
  return mealtimeOrder(a) - mealtimeOrder(b)
}

/** De-dupe + sort an arbitrary set of mealtimes into chronological order. */
export function sortMealtimes(mealtimes: Iterable<Mealtime>): Mealtime[] {
  return [...new Set(mealtimes)].sort(compareMealtimes)
}
